"use client";

import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

export type StatusState = "idle" | "loading" | "success" | "error";

interface StatusDialogProps {
	/** Controls whether the dialog is open */
	open: boolean;
	/** Fired when the dialog tries to close (or when user clicks action buttons) */
	onOpenChange: (open: boolean) => void;
	/** Current state of the dialog */
	state: StatusState;

	// --- Loading Props ---
	loadingTitle?: string;
	loadingDescription?: string;

	// --- Success Props ---
	successTitle?: string;
	successDescription?: string;
	successButtonText?: string;
	onSuccessAction?: () => void;

	// --- Error Props ---
	error?: string | null;
	errorTitle?: string;
	errorButtonText?: string;
	onErrorAction?: () => void;
}

export function StatusDialog({
	open,
	onOpenChange,
	state,

	loadingTitle = "Processing...",
	loadingDescription = "Please wait while we process your request.",

	successTitle = "Success!",
	successDescription = "Your action was completed successfully.",
	successButtonText = "Close",
	onSuccessAction,

	error = "An unexpected error occurred.",
	errorTitle = "Oops, something went wrong",
	errorButtonText = "Try Again",
	onErrorAction,
}: StatusDialogProps) {
	const handleSuccessClick = () => {
		if (onSuccessAction) onSuccessAction();
		else onOpenChange(false);
	};

	const handleErrorClick = () => {
		if (onErrorAction) onErrorAction();
		else onOpenChange(false);
	};

	if (state === "idle") return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="rounded-2xl sm:max-w-md text-center outline-none"
				showCloseButton={state !== "loading"}
				onInteractOutside={(e) => state === "loading" && e.preventDefault()}
				onEscapeKeyDown={(e) => state === "loading" && e.preventDefault()}
			>
				{state === "loading" && (
					<div className="py-8 space-y-4 outline-none">
						<div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full animate-pulse mx-auto">
							<Loader2 className="w-6 h-6 animate-spin text-primary" />
						</div>
						<DialogTitle className="text-xl font-bold">
							{loadingTitle}
						</DialogTitle>
						<DialogDescription className="text-sm text-muted-foreground mt-1">
							{loadingDescription}
						</DialogDescription>
					</div>
				)}

				{state === "error" && (
					<div className="py-8 space-y-4 outline-none">
						<div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mx-auto">
							<AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
						</div>
						<DialogTitle className="text-xl font-bold text-red-600 dark:text-red-400">
							{errorTitle}
						</DialogTitle>
						<DialogDescription className="text-sm text-muted-foreground mt-1">
							{error}
						</DialogDescription>
						<Button
							onClick={handleErrorClick}
							variant="outline"
							className="mt-4 rounded-xl w-full"
						>
							{errorButtonText}
						</Button>
					</div>
				)}

				{state === "success" && (
					<div className="py-8 space-y-4 outline-none">
						<DialogHeader className="items-center outline-none">
							<div className="bg-emerald-100 dark:bg-emerald-900/30 rounded-full p-4 w-fit mx-auto mb-2">
								<CheckCircle className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
							</div>
							<DialogTitle className="text-2xl font-bold">
								{successTitle}
							</DialogTitle>
							<DialogDescription className="text-base text-muted-foreground mt-1">
								{successDescription}
							</DialogDescription>
						</DialogHeader>
						<Button
							onClick={handleSuccessClick}
							variant="outline"
							className="mt-6 rounded-xl w-full"
						>
							{successButtonText}
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
